import { Button } from '@/components/ui/button'
import * as Card from '@/components/ui/card'
import * as Field from '@/components/ui/field'
import { Heading } from '@/components/ui/heading'
import { Input } from '@/components/ui/input'
import { Text } from '@/components/ui/text'
import { api } from '@api'
import AnimatedBackground from '@components/AnimatedBackground'
import { createForm, valiForm } from '@modular-forms/solid'
import { A, useNavigate } from '@solidjs/router'
import type { Session } from '@supabase/supabase-js'
import { createSignal, onCleanup, onMount } from 'solid-js'
import { ALLOWED_EMAILS } from 'src/constants/allowedEmails'
import { css } from 'styled-system/css'
import { Grid } from 'styled-system/jsx'
import { text } from 'styled-system/recipes'
import * as v from 'valibot'
import { setUser } from '../../state'
import { isProd } from 'src'

export const SignInSchema = v.object({
  email: v.pipe(
    v.string(),
    v.minLength(1, 'Email must be defined'),
    v.regex(/^\S+@\S+\.\S+$/, 'Please enter a valid email'),
  ),
  password: v.pipe(
    v.string(),
    v.minLength(1, 'Password must be defined'),
  ),
})

export type SignInValues = v.InferInput<typeof SignInSchema>

export default function SignIn() {
  const navigate = useNavigate()
  const [loading, setLoading] = createSignal(false)
  const [errorMsg, setErrorMsg] = createSignal('')

  const [form, { Form, Field: FormField }] = createForm<SignInValues>({
    validate: valiForm(SignInSchema),
    revalidateOn: 'change',
    initialValues: {
      email: '',
      password: '',
    },
  })

  const doLogin = (session: Session) => {
    setUser(session)
    navigate('/')
  }

  onMount(() => {
    const {
      data: { subscription },
    } = api().auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        navigate('/auth/update-password')
        return
      }
      if (session && event === 'SIGNED_IN') {
        // --- TEMPORARY WHITELIST START ---
        if (isProd && !ALLOWED_EMAILS.includes(session.user.email?.toLowerCase() || '')) {
          return // Ignore the event, RouteGuard will handle kicking them out
        }
        // --- TEMPORARY WHITELIST END ---
        doLogin(session)
      }
    })

    // Clean up the listener when the component unmounts
    onCleanup(() => {
      subscription.unsubscribe()
    })
  })

  const handleSignIn = async (values: SignInValues) => {
    setLoading(true)
    setErrorMsg('')

    // --- TEMPORARY WHITELIST START ---
    // TODO: Delete this check before launch
    if (isProd && !ALLOWED_EMAILS.includes(values.email.toLowerCase())) {
      setErrorMsg('Pardon our dust! We are actively migrating accounts. Check back soon.')
      setLoading(false)
      return
    }
    // --- TEMPORARY WHITELIST END ---

    const { error } = await api().auth.signInWithPassword({
      email: values.email,
      password: values.password,
    })

    if (error) setErrorMsg(error.message)
    setLoading(false)
  }

  return (
    <main
      class={css({
        display: 'grid',
        alignItems: { base: 'center', mdDown: 'start' },
        pt: { mdDown: 8 },
        justifyContent: 'center',
        height: '100vh',
        width: '100vw',
        position: 'relative',
        overflow: 'hidden',
        isolation: 'isolate', // Crucial: Keeps the z-index contained to this container
        bg: 'white', // Base color (or an ultra-light gray/jade)
      })}
    >
      <AnimatedBackground />
      <Card.Root minW={450}>
        <Card.Header display="flex" justifyContent="center" alignItems="center">
          <img
            class={css({ height: '40px', width: '40px' })}
            src={'/logo.png'}
            alt="sniper circle logo"
          />
          <Heading textStyle={{ base: 'lg' }} lineHeight="35px">
            The HVF Method
          </Heading>
        </Card.Header>

        <Card.Body>
          <Form onSubmit={handleSignIn}>
            <Grid gap={4}>
              <FormField name="email">
                {(field, formFieldProps) => (
                  <Field.Root invalid={!!field.error}>
                    <Field.Label>Email address</Field.Label>
                    <Input
                      {...formFieldProps}
                      value={field.value ?? ''}
                      type="email"
                      placeholder="Email address"
                      disabled={loading()}
                      autocomplete="email"
                    />
                    <Field.ErrorText>
                      {field.error && <div>{field.error}</div>}
                    </Field.ErrorText>
                  </Field.Root>
                )}
              </FormField>

              <FormField name="password">
                {(field, formFieldProps) => (
                  <Field.Root invalid={!!field.error}>
                    <Field.Label>Password</Field.Label>
                    <Input
                      {...formFieldProps}
                      value={field.value ?? ''}
                      type="password"
                      placeholder="Password"
                      disabled={loading()}
                      autocomplete="current-password"
                    />
                    <Field.ErrorText>
                      {field.error && <div>{field.error}</div>}
                    </Field.ErrorText>
                  </Field.Root>
                )}
              </FormField>

              {errorMsg() && (
                <p
                  class={`${text({ variant: 'danger' })} ${css({ color: 'red', fontSize: 'sm' })}`}
                >
                  {errorMsg()}
                </p>
              )}

              <Button
                type="submit"
                colorPalette="jade"
                loading={loading()}
                disabled={loading()}
              >
                {loading() ? 'Signing in...' : 'Sign In'}
              </Button>

              <Button type="button" variant="plain" size="xs">
                <A href="/auth/forgot-password">
                  <Text textStyle="xs">Forgot your password?</Text>
                </A>
              </Button>
            </Grid>
          </Form>
        </Card.Body>
      </Card.Root>
    </main>
  )
}
